import { z } from 'zod';
import { FlightSchema } from '@/types/benefits/flight/FlightFormData';
import { ActivitySchema } from '@/types/benefits/ActivityFormData';
import {
  HotelSchemaBase,
  hotelSuperRefine,
} from '@/types/benefits/hotel/HotelFormData';
import { CircuitSchema } from '@/types/circuits/CircuitFormData';
import { LOCALES } from './config';
import { VehicleWithChauffeurSchema } from '@/types/benefits/VehicleWithChauffeurFormData';
import { CarRentalSchema } from '@/types/benefits/carRental/CarRentalFormData';
import { BenefitTranslationSchema } from '@/types/circuits/CircuitDaysFormData';

export const multilingualFieldSchema = <T extends z.ZodType>(type: T) =>
  z.record(z.enum(LOCALES), type.optional());

export const FlightTranslationSchema = z.object({
  shortDescription: z.string().optional(),
  longDescription: z.string().optional(),
  benefit: z
    .object({
      name: z.string().optional(),
    })
    .optional(),
});

export const FlightSchemaWithTranslations = FlightSchema._def.schema.extend({
  translations: multilingualFieldSchema(FlightTranslationSchema),
});
export type FlightFormDataWithTranslations = z.infer<
  typeof FlightSchemaWithTranslations
>;

const ActivityTranslationSchema = z.object({
  shortDescription: z.string().optional(),
  longDescription: z.string().optional(),
  city: z.string().optional(),
  benefit: z
    .object({
      name: z.string().optional(),
    })
    .optional(),
});

export const ActivitySchemaWithTranslations = ActivitySchema._def.schema.extend(
  {
    translations: multilingualFieldSchema(ActivityTranslationSchema),
  },
);
export type ActivityFormDataWithTranslations = z.infer<
  typeof ActivitySchemaWithTranslations
>;

const VehicleWithChauffeurTranslationSchema = z.object({
  model: z.string().optional(),
  shortDescription: z.string().optional(),
  longDescription: z.string().optional(),
  benefit: z
    .object({
      name: z.string().optional(),
    })
    .optional(),
});

export const VehicleWithChauffeurSchemaWithTranslations =
  VehicleWithChauffeurSchema.extend({
    translations: multilingualFieldSchema(
      VehicleWithChauffeurTranslationSchema,
    ),
  });
export type VehicleWithChauffeurFormDataWithTranslations = z.infer<
  typeof VehicleWithChauffeurSchemaWithTranslations
>;

const CarOptionsSchema = z.object({
  insurances: z.array(
    z.object({
      name: z.string().optional(),
      shortDescription: z.string().optional(),
      longDescription: z.string().optional(),
    }),
  ),
  taxes: z.array(
    z.object({
      name: z.string().optional(),
    }),
  ),
  options: z.array(
    z.object({
      name: z.string().optional(),
      description: z.string().optional(),
    }),
  ),
  oneWayFees: z.array(
    z.object({
      name: z.string().optional(),
      description: z.string().optional(),
    }),
  ),
});

const CarRentalTranslationSchema = CarOptionsSchema.extend({
  benefit: z
    .object({
      name: z.string().optional(),
    })
    .optional(),
  carCategories: z
    .array(
      CarOptionsSchema.extend({
        category: z.string().optional(),
        model: z.string().optional(),
        description: z.string().optional(),
        included: z.string().optional(),
        notIncluded: z.string().optional(),
      }),
    )
    .optional(),
});

export const CarRentalSchemaWithTranslations = CarRentalSchema.extend({
  translations: multilingualFieldSchema(CarRentalTranslationSchema),
});
export type CarRentalFormDataWithTranslations = z.infer<
  typeof CarRentalSchemaWithTranslations
>;

const HotelTranslationSchema = z.object({
  shortDescriptionTitle: z.string().optional(),
  shortDescriptionBody: z.string().optional(),
  longDescriptionTitle: z.string().optional(),
  longDescriptionBody: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  chain: z.string().optional(),
  benefit: z
    .object({
      name: z.string().optional(),
    })
    .optional(),
  hotelRoomCategories: z
    .array(
      z.object({
        name: z.string().optional(),
        shortDescription: z.string().optional(),
        longDescription: z.string().optional(),
      }),
    )
    .optional(),
});

export const HotelSchemaWithTranslations = HotelSchemaBase.extend({
  translations: multilingualFieldSchema(HotelTranslationSchema),
}).superRefine(hotelSuperRefine);

export type HotelFormDataWithTranslations = z.infer<
  typeof HotelSchemaWithTranslations
>;

const CircuitTranslationSchema = z.object({
  name: z.string().optional(),
  summary: z.string().optional(),
  importantPoints: z.string().optional(),
  priceIncludes: z.string().optional(),
  priceExcludes: z.string().optional(),
  modificationConditions: z.string().optional(),
  cancellationConditions: z.string().optional(),
  circuitDays: z
    .array(
      z.object({
        stepTitle: z.string().optional(),
        description: z.string().optional(),
      }),
    )
    .optional(),
});

export const CircuitSchemaWithTranslations = CircuitSchema.extend({
  translations: multilingualFieldSchema(CircuitTranslationSchema),
});
export type CircuitFormDataWithTranslations = z.infer<
  typeof CircuitSchemaWithTranslations
>;

export type BenefitTranslation = z.infer<typeof BenefitTranslationSchema>;
